import { API_BASE_URL } from '@/config';

export interface UserAccess {
  hasAccess: boolean;
  accessType: string | null;
  grantedAt: string | null;
}

/**
 * Check if user has access to specific album content
 */
export const checkUserAccess = async (
  albumId: string, 
  token: string
): Promise<UserAccess> => {
  try {
    const response = await fetch(`${API_BASE_URL}/user/access/${albumId}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (response.ok) {
      return await response.json();
    } else {
      console.error('Failed to check user access');
      return { hasAccess: false, accessType: null, grantedAt: null };
    }
  } catch (err) {
    console.error('Error checking user access:', err);
    return { hasAccess: false, accessType: null, grantedAt: null };
  }
};

/**
 * Check if user can play an album based on access type and user permissions
 * CURRENTLY ONLY ENFORCED FOR SOUNDCHECK ALBUMS
 */
export const canPlayAlbum = async (
  albumId: string,
  albumAccessType: string,
  token?: string
): Promise<{ canPlay: boolean; reason?: string }> => {
  // If this is NOT a soundcheck album, allow access by default (for mobile compatibility)
  if (albumAccessType !== 'soundcheck') {
    return { canPlay: true };
  }
  
  // Only enforce access control for soundcheck albums
  // For premium content, user must be authenticated
  if (!token) {
    return { 
      canPlay: false, 
      reason: 'Authentication required for premium content' 
    };
  }

  // Check if user has purchased access
  const userAccess = await checkUserAccess(albumId, token);
  
  if (userAccess.hasAccess) {
    return { canPlay: true };
  }

  return { 
    canPlay: false, 
    reason: `${albumAccessType} access required - purchase needed` 
  };
};

/**
 * Get user's purchased albums
 */
export const getUserPurchases = async (token: string) => {
  try {
    const response = await fetch(`${API_BASE_URL}/user/purchases`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (response.ok) {
      return await response.json();
    } else {
      console.error('Failed to fetch user purchases');
      return [];
    }
  } catch (err) {
    console.error('Error fetching user purchases:', err);
    return [];
  }
}; 